/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import styles from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { FilledInput, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { CancelPresentationRounded, SaveRounded, ScatterPlot } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { ANCHOR_POINT_TYPES, HIDE_TARGET_ECD, MAIN_DEPTH_POINTS, TOOLTIPS } from '../../../../utils/constants';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import ModalDialog from '../../../ui/ModalDialog';
import { isMobile } from 'react-device-detect';
import { UNIT_SYSTEM } from '../../../../shared/config/hydraulics_constants';
import useUnsavedChanges from '../../../../hooks/useUnsavedChanges';
import clsx from 'clsx';
import MobileRunsECDSpan from './RunsECDSpan';
import MobileControlsTable from './ControlsTable';
import MobileWellsObservationPointsModal from './WellsObservationPoints';

const MobileWellboreTargetDialog = ({
  onCloseClick,
  isOpen,
  data,
  setTargetData,
  updateTargetData,
  onSave,
  anchorPoint,
  targetECD,
  hydraulicsRunUnits,
  targetData
}) => {
  const classes = styles();
  const dispatch = useDispatch();
  const [anchorType, setAnchorTypes] = useState(ANCHOR_POINT_TYPES);
  const [targetLoader, setTargetLoader] = useState(false);
  const [localAnchorPoint, setLocalAnchorPoint] = useState('');
  const [localTargetECD, setLocalTargetECD] = useState('');
  const [runsECDSpanNames, setRunsECDSpanNames] = useState(MAIN_DEPTH_POINTS);
  const RunsECDSpanUnits = hydraulicsRunUnits?.inputs?.ecd_span;
  const ControlsTableUnits = hydraulicsRunUnits?.inputs?.target_data?.controls_table;
  const [targetSaveEnable, setTargetSaveEnable] = useState(false);
  const [runsEcdDirty, setRunsEcdDirty] = useState(false);
  const [controlsTableDirty, setControlsTableDirty] = useState(false);
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges([], onCloseClick);
  const [wellsObservationPointsModal, setWellsObservationPointsModal] = useState(false);
  const observation = useSelector(state => state.hydraulicsLanding?.data?.observation_points);

  const closeHandler = () => {
    if (unSavedExists || targetSaveEnable || runsEcdDirty || controlsTableDirty) {
      handleClose(false);
      return;
    }
    onCloseClick();
  };

  useEffect(() => {
    const extractObservationNames = observation?.map(item => item.name);
    const updatedAnchorTypes = [...new Set([...ANCHOR_POINT_TYPES, ...extractObservationNames])];
    const updatedRunsECDSpanNames = [...new Set([...MAIN_DEPTH_POINTS, ...extractObservationNames])];
    setAnchorTypes(updatedAnchorTypes);
    setRunsECDSpanNames(updatedRunsECDSpanNames);
    setLocalTargetECD(targetECD);
    if (!updatedAnchorTypes.includes(anchorPoint)) {
      setLocalAnchorPoint('');
      setLocalTargetECD('');
    } else {
      setLocalAnchorPoint(anchorPoint);
    }
  }, []);

  useEffect(() => {
    if (HIDE_TARGET_ECD.includes(localAnchorPoint)) {
      setLocalTargetECD(null);
    }
  }, [localAnchorPoint]);

  const getModalTitle = () => {
    return (
      <Grid container alignItems="center" justifyContent={isMobile ? 'space-between' : 'flex-end'}>
        {isMobile
          ? (
          <Grid item>
            <Typography fontSize='18px' fontWeight='bold'>Run&apos;s Target Settings</Typography>
          </Grid>
            )
          : null}
        <Grid item>
          <Tooltip title={TOOLTIPS.wellsObservationPoints}>
            <span>
              <IconButton
                onClick={() => {
                  setWellsObservationPointsModal(true);
                }}>
                <ScatterPlot fontSize='medium' />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            variant="solid"
            disabled={!targetSaveEnable}
            onClick={() => {
              if (localAnchorPoint !== 'None' && targetData?.ecd_span?.length > 0 && !targetData.controls_table?.length) {
                dispatch(
                  enqueueSnackbar(
                    'Controls Table is also required when EMW Limits is specified',
                    'error',
                    new Date().getTime() + Math.random()
                  )
                );
                return;
              }
              const isNone = HIDE_TARGET_ECD.includes(localAnchorPoint);

              setTargetLoader(true);
              setTimeout(() => setTargetLoader(false), 500);

              let updatedTargetData = {};

              if (!isNone && !checkTargetECD()) {
                return;
              }

              if (!isNone) {
                updatedTargetData = {
                  anchor_point: localAnchorPoint,
                  target_ecd: Number(localTargetECD)
                };
              } else {
                updatedTargetData = {
                  anchor_point: localAnchorPoint,
                  target_ecd: null
                };
              }

              setTargetData(prev => {
                const shouldClearEcdSpan =
                  localAnchorPoint === 'None' &&
                  Array.isArray(prev.controls_table) &&
                  prev.controls_table.length === 0;

                return {
                  ...prev,
                  ...updatedTargetData,
                  ...(shouldClearEcdSpan && { ecd_span: [] })
                };
              });
              onCloseClick();
              // to set isDirty
              updateTargetData(data);
              onSave(localAnchorPoint, localTargetECD);
            }}
          >
            <SaveRounded fontSize='medium' />
          </IconButton>
          <IconButton
            onClick={closeHandler}
          >
            <CancelPresentationRounded fontSize='medium' />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const checkTargetECD = () => {
    const minECD = 5;
    const maxECD = 25;
    const unit = 'ppg';

    const isAnchorPointEmpty = localAnchorPoint === '' || localAnchorPoint === null || localAnchorPoint === undefined;
    const isTargetECDEmpty = localTargetECD === '' || localTargetECD === null || localTargetECD === undefined;

    if (isAnchorPointEmpty && isTargetECDEmpty) {
      dispatch(
        enqueueSnackbar(
          'Please select Anchor Point and enter Target ECD',
          'error',
          Date.now() + Math.random()
        )
      );
      return false;
    }

    if (isAnchorPointEmpty) {
      dispatch(
        enqueueSnackbar(
          'Please choose Anchor Point',
          'error',
          Date.now() + Math.random()
        )
      );
      return false;
    }

    if (isTargetECDEmpty) {
      dispatch(
        enqueueSnackbar(
          'Please enter Target ECD',
          'error',
          Date.now() + Math.random()
        )
      );
      return false;
    }

    const targetECDValue = Number(localTargetECD);
    if (isNaN(targetECDValue) || targetECDValue < minECD || targetECDValue > maxECD) {
      dispatch(
        enqueueSnackbar(
          `Target ECD must be a number between ${minECD} and ${maxECD} ${unit}`,
          'error',
          Date.now() + Math.random()
        )
      );
      return false;
    }

    return true;
  };

  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={onCloseClick}
      dialogClass={clsx(classes.dialogModal, localAnchorPoint === 'None' ? classes.dialogMinWidth : classes.dialogMaxWidth)}
    >
      <Grid item className={classes.targetContainer} maxWidth='755px'>
        <Grid container direction="column" margin={'6px'}>
          {!isMobile
            ? (
              <Grid item>
                <Typography variant="h5">Run&apos;s Target Settings</Typography>
              </Grid>
              )
            : null}
          <Grid container flexDirection={'column'} sx={{ margin: '10px 0' }}>
          <Grid flex={1} container item alignItems="center">
            <Grid item xs={4} md={3}>
              <Typography fontWeight="700">Anchor Point</Typography>
            </Grid>
            <Grid item xs={6} md={6} sx={{ ml: 2 }}>
              <FormControl fullWidth variant="filled" disabled={targetLoader || runsEcdDirty || controlsTableDirty}>
                <InputLabel id="anchor-point-label">Select Anchor Point</InputLabel>
                <Select
                  labelId="anchor-point-label"
                  id="anchor-point-select"
                  value={localAnchorPoint}
                  onChange={(e) => {
                    setLocalAnchorPoint(e.target.value);
                    setTargetSaveEnable(true);
                  }}
                  disableUnderline
                >
                  {anchorType?.map((item, index) => (
                    <MenuItem key={`item_${item}_${index}`} value={item}>{item}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid flex={1} container item alignItems="center" sx={{ marginTop: 2 }}>
            <Grid item xs={4} md={4}>
              {!HIDE_TARGET_ECD.includes(localAnchorPoint) && (
                <Typography fontWeight="700">
                  Target ECD (ppg)
                </Typography>
              )}
            </Grid>
            <Grid item xs={6} md={6} sx={{ ml: 2 }}>
              {!HIDE_TARGET_ECD.includes(localAnchorPoint) && (
                <FormControl fullWidth variant="filled" disabled={targetLoader || runsEcdDirty || controlsTableDirty}>
                  <InputLabel htmlFor="target-ecd">Enter Target ECD</InputLabel>
                  <FilledInput
                    id="target-ecd"
                    value={localTargetECD}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        setLocalTargetECD(value);
                        setTargetSaveEnable(true);
                      }
                    }}
                    disableUnderline
                  />
                </FormControl>
              )}
            </Grid>
          </Grid>
          </Grid>
        </Grid>
      </Grid>
      {localAnchorPoint !== 'None' && (
        <>
          <MobileRunsECDSpan
            units={RunsECDSpanUnits}
            currentUnit={UNIT_SYSTEM.US}
            runsECDSpanNames={runsECDSpanNames}
            loading={false}
            targetData={targetData}
            setTargetData={setTargetData}
            setParameterDirty={() => { }}
            setTargetSaveEnable={setTargetSaveEnable}
            setRunsEcdDirty={setRunsEcdDirty}
            disableTable={controlsTableDirty}
          />
          {targetData?.ecd_span?.length > 0
            ? (
              <MobileControlsTable
                units={ControlsTableUnits}
                currentUnit={UNIT_SYSTEM.US}
                runsECDSpanNames={runsECDSpanNames}
                loading={false}
                targetData={targetData}
                setTargetData={setTargetData}
                setParameterDirty={() => { }}
                setTargetSaveEnable={setTargetSaveEnable}
                setControlsTableDirty={setControlsTableDirty}
                disableTable={runsEcdDirty}
              />
              )
            : null}
        </>
      )}
      {wellsObservationPointsModal
        ? (
          <MobileWellsObservationPointsModal
            data={data}
            isOpen={wellsObservationPointsModal}
            onCloseClick={() => {
              setWellsObservationPointsModal(false);
            }}
            anchorPoint={localAnchorPoint}
          />
          )
        : null}
       <UnsavedChangesModal />
    </ModalDialog>
  );
};
MobileWellboreTargetDialog.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  selectedJob: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.any,
  setTargetData: PropTypes.func,
  updateTargetData: PropTypes.func,
  anchorPoint: PropTypes.any,
  targetECD: PropTypes.any,
  onSave: PropTypes.func,
  hydraulicsRunUnits: PropTypes.any,
  targetData: PropTypes.any
};

export default MobileWellboreTargetDialog;
